import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Repeat } from "lib/@components/repeat"
import { Sortable } from "lib/@components/Sortable"
import { useMemo } from "react"
import Iconify from "minimals-template/components/Iconify"
import { promotePinned } from "lib/@components/managed-columns/column-state.mjs"
import { ManageColumnRow } from "./manage-column-row"

export function ManageColumnsPanel({ onClose }) {
    const { allColumns, columnState, resetColumns, applyColumnOrder } = useBoundContext()
    const orderedAll = useMemo(computeOrderedAll, [allColumns, columnState])

    return (
        <Box
            sx={{ display: "flex", flexDirection: "column", height: 1, minHeight: 0 }}
            data-cy="manage-columns-panel"
            role="group"
            aria-label="Manage columns"
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: 1.5, py: 1, borderBottom: "1px solid", borderBottomColor: "divider" }}
            >
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="subtitle2">Manage columns</Typography>
                    <Tooltip
                        arrow
                        title={
                            <>
                                Drag columns to change their order, or pin a column to keep it at the left of the table.
                                Columns with no data are hidden automatically.
                            </>
                        }
                    >
                        <Box component="span" sx={{ display: "inline-flex" }} data-cy="manage-columns-info">
                            <Iconify icon="mdi:information-outline" width={15} sx={{ color: "text.disabled" }} />
                        </Box>
                    </Tooltip>
                </Stack>
                <Stack direction="row">
                    <Tooltip title="Reset columns to default">
                        <IconButton size="small" onClick={resetColumns} data-cy="manage-columns-reset">
                            <Iconify icon="mdi:refresh" width={18} />
                        </IconButton>
                    </Tooltip>
                    <IconButton aria-label="Close" size="small" onClick={onClose} data-cy="manage-columns-close">
                        <Iconify icon="mdi:close" width={18} />
                    </IconButton>
                </Stack>
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                <Sortable items={[...orderedAll]} id="field" onOrdered={handleReorder}>
                    <Repeat list={orderedAll} item={<ManageColumnRow />} keyFn={getField} />
                </Sortable>
            </Box>
        </Box>
    )

    function computeOrderedAll() {
        const indexByField = new Map()
        for (const c of allColumns) indexByField.set(c.field, c)

        const ordered = (columnState?.order ?? []).map(lookupColumn).filter(Boolean)
        return promotePinned(ordered, columnState?.pinnedField)

        function lookupColumn(f) {
            return indexByField.get(f)
        }
    }

    function handleReorder(newOrder) {
        applyColumnOrder(newOrder.map(getField))
    }

    function getField(column) {
        return column.field
    }
}
